import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  CreatureInstance,
  SummonInstance,
  RollPlus,
  TransformInstance,
  RolledValue,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import ButtonBox from "@components/input/buttons/ButtonBox";
import Roller from "@dndComponents/other/Roller";
import ToastBox from "@components/display/ToastBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import { AbilityScoreType } from '@dndModels/Types';

interface Props {
  disabled: boolean;
  trim?: boolean;
  obj: CreatureInstance | SummonInstance | TransformInstance;
  color?: string;
  onChange: () => void;
}

const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

const darks = ["red","green","blue","purple","black","gray"];

export default function CreatureInstanceSavingThrows({
  obj,
  disabled,
  trim,
  color,
  onChange
}: Props) {
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [popoverSave, setPopoverSave] = useState("");
  const [popoverTime, setPopoverTime] = useState(-1);
  const [open, setOpen] = useState(false);

  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        lineHeight: 2.5,
        '&:hover': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.main,
        },
        '&.disabled': {
          color: theme.palette.primary.contrastText,
          backgroundColor: "gray",
        },
        '&:img': {
          filter: "brightness(0) invert(1)"
        }
      },
    });
  });
  const { classes, cx } = useStyles();

  const renderSavingThrow = (ability: AbilityScoreType) => {
    return (
      <Grid>
        <div className="curved_border" style={{
            padding: "4px"
          }}>
          <Grid container spacing={0} direction="row" style={{ margin: "4px" }}>
            <Grid
              size={{
                xs: trim ? 7 : 9
              }}>
              <div style={{ float: "left", lineHeight: "2.2" }} className="header">
                { ability }
              </div>
            </Grid>
            <Grid
              size={{
                xs: trim ? 5 : 3
              }}>
              <ButtonBox 
                name={`${common_util.addPlusMaybe(obj.get_saving_throw(ability))}`}
                disabled={ disabled } 
                color={ color && !darks.includes(color) ? "black" : (color ? "white" : undefined) }
                onClick={() => {
                  const rolls: RollPlus[] = [];
                  const roll_plus = new RollPlus();
                  roll_plus.flat = +obj.get_saving_throw(ability);
                  rolls.push(roll_plus);
                  // console.log(rolls);
                  const new_roll = char_util.rollD20({ char_interface: obj, rolls, type: "Saving Throw", advantage: obj instanceof CreatureInstance || obj instanceof SummonInstance ? 0 : obj.advantage });
                  setRoll(new_roll);
                  setPopoverSave(ability);
                  setPopoverTime(common_util.get_time_number());
                }} 
              />
            </Grid>
          </Grid>
        </div>
      </Grid>
    );
  }

  const renderExtras = () => {
    return (
      <span className={cx(classes.root)} >
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
            obj.subscription_holder.modalClosed();
          }}>
          <span>Saving Throw Details</span>
        </ModalOrDrawer>
        <ToastBox
          openTime={ popoverTime }
        >
          { renderRoll() }
        </ToastBox>
      </span>
    );
  }

  const renderRoll = () => {
    return (
      <Roller 
        name={popoverSave}
        rolled_values={roll}
        type="Saving Throw" 
      />
    );
  }

  return (
    <Grid
      key="SavingThrows"
      container
      spacing={0}
      direction="column"
      style={ color ? { 
        backgroundColor: color,
        color: darks.includes(color) ? "white" : "black",
      } : {}}
      className={ color ? "curved_border" : "curved_border bg_black_transp"}>
      { trim ? 
        <Grid container spacing={0} direction="row">
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 2
            }}>
            { renderSavingThrow("STR") }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 2
            }}>
            { renderSavingThrow("DEX") }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 2
            }}>
            { renderSavingThrow("CON") }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 2
            }}>
            { renderSavingThrow("INT") }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 2
            }}>
            { renderSavingThrow("WIS") }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 2
            }}>
            { renderSavingThrow("CHA") }
          </Grid>
        </Grid>
      :
        <>
          <Grid className="centered">
            <div>
              Saving Throws
            </div>
          </Grid>
          <Grid
            className="centered"
            style={{
              fontSize: "11px"
            }}>
            <div style={{ width: "245px" }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  container
                  spacing={0}
                  direction="column"
                  size={{
                    xs: 6
                  }}>
                  { renderSavingThrow("STR") }
                  { renderSavingThrow("DEX") }
                  { renderSavingThrow("CON") }
                </Grid>
                <Grid
                  container
                  spacing={0}
                  direction="column"
                  size={{
                    xs: 6
                  }}>
                  { renderSavingThrow("INT") }
                  { renderSavingThrow("WIS") }
                  { renderSavingThrow("CHA") }
                </Grid>
              </Grid>
            </div>
          </Grid>
        </>
      }
      { renderExtras() }
    </Grid>
  );
}
